import { boundMethod } from 'autobind-decorator';
import { authManager } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';
import { Sentry } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { v4 as uuidv4 } from 'uuid';


import {
  InboundWebsocketEvent,
  WebsocketMessageType,
} from '../types/inboundWebsocketEvent';
import { getWorkerConnection } from '../worker/getWorkerConnection';
import {
  GetDataFulfilledMessage,
  PortId,
  WorkerMessage,
  WorkerRequestMessage,
} from '../worker/types';

import { SocketInterface } from './types';

type onPrimaryConnectionChange = (isActive: boolean) => void;

export class SharedWorkerImplementation implements SocketInterface {
  private _worker = getWorkerConnection();
  private _isConnected = false;
  private _onWebSocketClosed = noop;
  private _portId: PortId | null = null;
  private _previousPortId: PortId | null = null;
  private _onPrimaryConnectionChange: onPrimaryConnectionChange = noop;

  get isConnected(): boolean {
    return this._isConnected;
  }

  get portId(): PortId | null {
    return this._portId;
  }

  private postMessage(message: WorkerMessage) {
    this._worker.port.postMessage(message);
  }

  async start(): Promise<void> {
    const authData = await authManager.getAuthData();

    if (authData?.access_token) {
      this.postMessage({
        type: 'worker:connect-to-websocket',
        payload: {
          appToken: authData?.access_token,
          websocketUrl: config.url.webSocket,
        },
      });
    }

    // Notify worker that this instance is disconnecting
    window.addEventListener('beforeunload', () => {
      this.postMessage({
        type: 'worker:disconnect-port',
        payload: { id: this._portId as string },
      });

      this.sendAndWaitForResponse('worker:get-number-of-connections').then(
        (response) => {
          const { payload } = response as {
            payload: { numberOfConnections: number };
          };

          if (ampli.isLoaded) {
            ampli.sharedWorkerTabOrWindowClosed({
              numberOfInstances: payload.numberOfConnections,
            });
          }
        },
      );
    });

    // Notify worker when the window / tab is focused (active)
    window.addEventListener('focus', this.onWindowFocusHandler);
  }

  close(): void {
    this.postMessage({
      type: 'worker:close-websocket',
    });

    this.onError(noop);
    window.removeEventListener('focus', this.onWindowFocusHandler);
  }

  @boundMethod
  private onWindowFocusHandler() {
    this.postMessage({
      type: 'worker:port-activated',
      payload: {
        id: this._portId as string,
      },
    });
  }

  private waitForMessageAndResolve(
    resolve: (message) => void,
    predicate: (message) => boolean,
  ) {
    const listener = (event) => {
      try {
        const message: InboundWebsocketEvent | WorkerMessage =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (predicate(message)) {
          this._worker.port.removeEventListener('message', listener);
          resolve(message as GetDataFulfilledMessage);
        }
      } catch (e) {
        console.warn('Error parsing worker message', event);
        Sentry.addBreadcrumb({
          category: 'sharedWorker',
          message: 'Error parsing worker message',
          level: 'warning',
          data: { event },
        });
      }
    };

    this._worker.port.addEventListener('message', listener);
  }

  /**
   * Returns a promise that resolves once a specific message is sent from the
   * websocket.
   *
   * @param type The message type which will be compared against incoming
   *   messages from the websocket.
   */
  waitForMessage(type: WebsocketMessageType): Promise<InboundWebsocketEvent> {
    return new Promise((resolve) => {
      this.waitForMessageAndResolve(
        resolve,
        (message) => message?.type === type,
      );
    });
  }

  send(message: WorkerMessage): void {
    if (this._isConnected) {
      this.postMessage(message);
    } else {
      console.warn('WS: ws is not ready');
      Sentry.addBreadcrumb({
        category: 'sharedWorker',
        message: 'WS: ws is not ready',
        level: 'warning',
      });
    }
  }

  sendAndWaitForResponse(type: WorkerRequestMessage['type']): Promise<unknown> {
    return new Promise((resolve) => {
      const id = uuidv4();

      this.waitForMessageAndResolve(
        resolve,
        (message: WorkerRequestMessage) => message?.payload?.requestId === id,
      );

      this.postMessage({
        type,
        payload: { requestId: id },
      } as WorkerRequestMessage);
    });
  }

  onClose(handler: () => void): void {
    this._onWebSocketClosed = handler;
  }

  onMessage(
    onMessageHandler: (message: WorkerMessage | InboundWebsocketEvent) => void,
  ): void {
    this._worker.port.onmessage = (event) => {
      const data: WorkerMessage | InboundWebsocketEvent =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      if ('type' in data) {
        switch (data.type) {
          case 'worker:port-connected':
            this._portId = data.payload.id;

            this.sendAndWaitForResponse(
              'worker:get-number-of-connections',
            ).then((response) => {
              const { payload } = response as {
                payload: { numberOfConnections: number };
              };

              if (ampli.isLoaded) {
                ampli.sharedWorkerTabOrWindowOpened({
                  numberOfInstances: payload.numberOfConnections,
                });
              }
            });

            break;
          case 'worker:connected-to-websocket':
            this._isConnected = true;
            break;
          case 'worker:websocket-closed':
            Sentry.captureMessage('WebSocket: Close event received', {
              level: 'log',
              extra: data.payload,
            });
            ampli.websocketCloseEventReceived(data.payload);

            this._isConnected = false;
            this._onWebSocketClosed();
            break;
          case 'worker:primary-port-changed':
            if (
              data.payload.id === this._portId &&
              this._previousPortId !== this.portId &&
              ampli.isLoaded
            ) {
              ampli.sharedWorkerTabOrWindowChanged();
            }

            this._onPrimaryConnectionChange(data.payload.id === this._portId);
            this._previousPortId = data.payload.id;
            break;
        }
      }

      onMessageHandler(data);
    };
  }

  onError(onErrorHandler: (error: Event) => void): void {
    this._worker.onerror = (error) => {
      console.warn('WS: Error event received', error);
      Sentry.addBreadcrumb({
        category: 'sharedWorker',
        message: 'WS: Error event received',
        level: 'warning',
        data: { error },
      });
      onErrorHandler(error);
    };
  }

  onPrimaryConnectionChange(handler: onPrimaryConnectionChange): void {
    this._onPrimaryConnectionChange = handler;
  }
}
